// Self-contained motion, scroll and reveal helpers for the Applied Materials site.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { animate, motion, useInView, useReducedMotion } from "framer-motion";
import Lenis from "lenis";

const EASE = [0.22, 1, 0.36, 1] as const;
const VIEW = { once: true, amount: 0.15, margin: "100000px 0px 0px 0px" } as const;

export function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

let lenisInstance: Lenis | null = null;

export function useLenis() {
  useEffect(() => {
    if (prefersReducedMotion() || window.matchMedia("(pointer: coarse)").matches) return;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisInstance = lenis;
    let raf = 0;
    const loop = (t: number) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisInstance = null;
    };
  }, []);
}

export function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (!el) return;
  if (lenisInstance) {
    lenisInstance.resize();
    lenisInstance.scrollTo(el);
  } else {
    el.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "start" });
  }
  window.history.replaceState(null, "", `#${id}`);
  el.querySelector<HTMLElement>("[data-section-heading]")?.focus({ preventScroll: true });
}

export function useScrollSpy(ids: string[]) {
  const [active, setActive] = useState(ids[0]);
  const key = ids.join(",");
  useEffect(() => {
    const list = key.split(",");
    let raf = 0;
    const update = () => {
      raf = 0;
      const line = window.innerHeight * 0.4;
      let current = list[0];
      list.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      setActive(current);
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [key]);
  return active;
}

export function Reveal({ children, className = "", delay = 0, as = "div", testId }: { children: ReactNode; className?: string; delay?: number; as?: "div" | "li"; testId?: string }) {
  const Tag = as === "li" ? motion.li : motion.div;
  return (
    <Tag className={className} data-testid={testId} initial={{ opacity: 0.6, y: 28 }} whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }} transition={{ duration: 0.8, delay, ease: EASE }}>
      {children}
    </Tag>
  );
}

// Counts a printed figure (">99%", "$17.8M", "~36,500") up from zero the first time it scrolls into view.
export function CountUp({ value, className, duration = 1.4 }: { value: string | number; className?: string; duration?: number }) {
  const str = String(value);
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, VIEW);
  const armed = useRef(false);
  const [text, setText] = useState<string | null>(null);
  const m = /^(19|20)\d\d$/.test(str) ? null : str.match(/^([^\d]*)([\d,]*\.?\d+)(.*)$/);
  const target = m ? parseFloat(m[2].replace(/,/g, "")) : 0;
  const decimals = m?.[2].split(".")[1]?.length ?? 0;
  const commas = !!m?.[2].includes(",");
  const fmt = (v: number) => `${m?.[1] ?? ""}${commas ? Math.round(v).toLocaleString("en-US") : v.toFixed(decimals)}${m?.[3] ?? ""}`;

  useEffect(() => {
    const el = ref.current;
    if (!m || !target || reduce || !el || el.getBoundingClientRect().top < window.innerHeight) return;
    armed.current = true;
    setText(fmt(0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!armed.current || !inView) return;
    armed.current = false;
    const controls = animate(0, target, { duration, ease: EASE, onUpdate: (v) => setText(fmt(v)), onComplete: () => setText(null) });
    return () => {
      controls.stop();
      setText(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  return (
    <span ref={ref} className={`tabular-nums ${className ?? ""}`}>
      <span aria-hidden>{text ?? str}</span>
      <span className="sr-only">{str}</span>
    </span>
  );
}

export const growX = (delay = 0) => ({
  initial: { scaleX: 0 },
  whileInView: { scaleX: 1 },
  viewport: VIEW,
  transition: { duration: 1.1, delay, ease: EASE },
});

export const growY = (delay = 0) => ({
  initial: { scaleY: 0 },
  whileInView: { scaleY: 1 },
  viewport: VIEW,
  transition: { duration: 1.1, delay, ease: EASE },
});

export function Wipe({ children, delay = 0 }: { children: ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, VIEW);
  const reduce = useReducedMotion();
  return (
    <div ref={ref}>
      <motion.div
        initial={reduce ? false : { clipPath: "inset(0 100% 0 0)" }}
        animate={inView || reduce ? { clipPath: "inset(0 0% 0 0)" } : undefined}
        transition={{ duration: 1.2, delay, ease: EASE }}
      >
        {children}
      </motion.div>
    </div>
  );
}
